import { BriefcaseIcon, BuildingOffice2Icon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useMemo, useRef } from 'react';

import HighlightSelect, { type SelectOption } from '../../../components/HighlightSelect';
import type { RBACUser } from '../../../types';
import {
  departmentOptions,
  filterReferenceCandidates,
  REGIONS,
  type RegionCode,
} from '../copyRoles';

import UserSummary from './UserSummary';

type Props = {
  users: RBACUser[];
  primaryUser: RBACUser;
  region: RegionCode | '';
  department: string;
  query: string;
  onRegionChange: (region: RegionCode | '') => void;
  onDepartmentChange: (department: string) => void;
  onQueryChange: (query: string) => void;
  onSelect: (user: RBACUser) => void;
};

// Step 1 of Copy Roles: find a peer to copy from. Narrows the (already-loaded) user list
// by region, department, and a job-title substring — see filterReferenceCandidates. All
// client-side; picking a candidate hands it back up to the modal to load their roles.
export default function ReferenceUserPicker({
  users,
  primaryUser,
  region,
  department,
  query,
  onRegionChange,
  onDepartmentChange,
  onQueryChange,
  onSelect,
}: Props) {
  const departments = useMemo(() => departmentOptions(users), [users]);
  const candidates = useMemo(
    () =>
      filterReferenceCandidates(users, primaryUser.id, {
        department: department || undefined,
        query,
        region: region || undefined,
      }),
    [users, primaryUser.id, region, department, query]
  );

  const regionOptions = useMemo<SelectOption[]>(
    () => [
      { label: 'Any region', value: '' },
      ...REGIONS.map((r) => ({ label: r.label, value: r.code })),
    ],
    []
  );
  const departmentSelectOptions = useMemo<SelectOption[]>(
    () => [
      { label: 'Any department', value: '' },
      ...departments.map((d) => ({ label: d, value: d })),
    ],
    [departments]
  );

  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // ArrowDown from the search field jumps into the first candidate.
  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      listRef.current?.querySelector<HTMLElement>('button')?.focus();
    }
  };

  // Arrows move between candidates; arrow up from the first returns to the search field.
  const onCandidateKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = e.currentTarget.closest('li')?.nextElementSibling;
      next?.querySelector<HTMLElement>('button')?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const previous = e.currentTarget.closest('li')?.previousElementSibling;
      const previousButton = previous?.querySelector<HTMLElement>('button');
      if (previousButton) previousButton.focus();
      else searchRef.current?.focus();
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <p className="text-sm opacity-70 mb-3">
        Pick a colleague in the same position to copy roles from — narrow by region, department, or
        job title, then choose the closest match. You&apos;ll review exactly which roles get copied
        before anything is assigned.
      </p>

      <div className="border-l-4 border-primary bg-base-200 rounded-r-md px-3 py-2 mb-4">
        <UserSummary label="Assigning roles to" user={primaryUser} />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-3">
        <HighlightSelect
          label="Region"
          className="sm:w-44"
          value={region}
          options={regionOptions}
          onChange={(value) => onRegionChange(value as RegionCode | '')}
        />

        <HighlightSelect
          label="Department"
          className="sm:w-56"
          value={department}
          options={departmentSelectOptions}
          onChange={onDepartmentChange}
        />

        <label className="flex flex-col gap-1 w-full flex-1">
          <span className="text-xs font-medium opacity-70">Name or job title</span>
          <input
            ref={searchRef}
            type="text"
            className="input input-bordered input-sm w-full"
            placeholder="Search name or job title…"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={onSearchKeyDown}
          />
        </label>
      </div>

      <div className="text-xs opacity-60 mb-2">
        {candidates.length} {candidates.length === 1 ? 'match' : 'matches'}
      </div>

      <div className="border border-base-200 rounded-lg overflow-hidden flex-1 min-h-0">
        {candidates.length === 0 ? (
          <div className="p-4 text-sm opacity-60">
            No matching users. Widen the region/department or clear the job-title search.
          </div>
        ) : (
          <ul ref={listRef} className="divide-y divide-base-200 h-full overflow-y-auto">
            {candidates.map((candidate) => (
              <li key={candidate.id}>
                {/* Hover/keyboard-focus paints the row primary (purple in the dark theme),
                    matching the Users-page user search; arrow keys move between rows. */}
                <button
                  type="button"
                  className="group w-full text-left p-2 focus:outline-none hover:bg-primary focus:bg-primary hover:text-primary-content focus:text-primary-content"
                  onClick={() => onSelect(candidate)}
                  onKeyDown={onCandidateKeyDown}
                >
                  <div className="grid grid-cols-[15rem_minmax(0,1fr)] items-center gap-x-4 gap-y-0.5">
                    <div className="font-semibold truncate">{candidate.displayName}</div>
                    <div className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-info group-hover:text-primary-content group-focus:text-primary-content">
                      {candidate.jobTitle && (
                        <>
                          <BriefcaseIcon className="h-3.5 w-3.5 shrink-0 text-base-content opacity-70 group-hover:text-primary-content group-focus:text-primary-content" />
                          <span className="truncate">{candidate.jobTitle}</span>
                        </>
                      )}
                    </div>
                    <div className="flex min-w-0 items-center gap-1.5 text-xs opacity-70">
                      <EnvelopeIcon className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{candidate.userPrincipalName}</span>
                    </div>
                    <div className="flex min-w-0 items-center gap-1.5 text-xs opacity-70">
                      {(candidate.department || candidate.officeLocation) && (
                        <>
                          <BuildingOffice2Icon className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">
                            {[candidate.department, candidate.officeLocation]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
